/**
 * The mobile Brief card for guidance by season (issue #387): how many of the earnings calls on
 * record raised, kept or lowered their outlook in the most recent reporting season.
 *
 * ONE card. The season, the headline and every count come from `src/shared/guidanceSeason.js`
 * — the same module the desktop Market Analysis "Guidance" card renders from — so the two
 * surfaces cannot disagree about which season is "last" or what it says. Pure: `App.js` feeds it
 * the `/earnings-highlights/guidance-season` payload and drops the result into the Brief.
 */
import {
  pickSeason,
  seasonTitle,
  seasonHeadline,
  seasonOthersLine,
  seasonScopeLine,
  SEASON_DIRECTIONS,
  DIRECTION_WORDS,
  SEASON_NOTE,
} from '../shared/guidanceSeason';
import { GUIDANCE_STYLE } from '../shared/callHighlightStyles';
import { stackedBarVisual } from './briefVisuals';

/**
 * Bar segments for any raised/kept/lowered counts object — shared by the season-level bar (this
 * file) and, on desktop, each industry's own bar (`GuidanceSeasonCard.jsx`), so a count and its
 * segment can never drift apart.
 */
export const countsToSegments = (counts) =>
  SEASON_DIRECTIONS.map((key) => ({
    key,
    label: DIRECTION_WORDS[key],
    count: counts[key],
    color: GUIDANCE_STYLE[key].bar,
  }));

/** The bar's segments for a season — shared with the desktop card so both draw the same split. */
export const seasonSegments = (season) => countsToSegments(season.counts);

/**
 * @param data the `/earnings-highlights/guidance-season` payload
 * @returns [] or ONE Brief item. Empty when no season has enough calls to be a read.
 */
export function buildGuidanceSeasonCard(data) {
  const season = pickSeason(data?.seasons);
  if (!season) return [];
  const visual = stackedBarVisual(seasonSegments(season));
  if (!visual) return [];

  const headline = seasonHeadline(season);
  // ONE line: the tape renders subtitles through renderMarketText, which collapses newlines.
  // The counts are drawn (the visual), so the text carries only what the bar cannot: the groups
  // outside the ratio, the scope, and where the population comes from.
  const expanded = [seasonOthersLine(season), seasonScopeLine(season), SEASON_NOTE]
    .filter(Boolean)
    .join(' — ');

  return [{
    type: 'guidanceSeason',
    id: `guidance-season-${season.key}`,
    label: `Guidance · ${seasonTitle(season)}`,
    title: headline,
    fullTitle: headline,
    fullSubtitle: expanded,
    visual,
  }];
}
